/**
 * Superficies "glassmorphism" centralizadas (personal = naranja, autónomos = lila).
 *
 * Fuente ÚNICA de verdad para el estilo de cards, cabeceras y navbar / botones
 * fijos que quedan por encima del fondo degradado. Para cambiar color, sombra,
 * blur o transparencia de cualquiera de esas superficies se toca SOLO este fichero.
 *
 * El cambio naranja -> lila es automático: cada superficie lleva un override de
 * sombra bajo el ancestro `.autonomos-scope`, que marca GlobalLayout cuando
 * `state.isAutonomos` es true (misma señal con la que se elige el fondo). Los
 * consumidores solo tienen que hacer spread del objeto en su clase de estilos:
 *
 *   import { CARD_SURFACE } from '../../../styles/surfaces.styles';
 *   export const misEstilos = makeStyles({
 *       card: { ...CARD_SURFACE, padding: 16 },
 *   });
 */

const SHADOW_BORDER = '0 0 0 1px rgba(207, 207, 207, 0.08)';
const SHADOW_PERSONAL = 'rgba(247, 95, 43, 0.10)';
const SHADOW_AUTONOMOS = 'rgba(133, 102, 204, 0.08)';

/**
 * Desenfoques del sistema. Cards y menú lateral 7px; cabeceras y cajas 4px;
 * el menu inferior (tab bar) 8px; y el velo del menú lateral 12px, que es el
 * que oculta la pantalla entera.
 */
const BLUR = 'blur(4px)';
const CARD_MENU_BLUR = 'blur(7px)';
const NAVBAR_BLUR = 'blur(8px)';
const MENU_OVERLAY_BLUR = 'blur(12px)';

/** Cards / tarjetas: borde 1px, fondo 50%, sombra 1px 1px. */
export const CARD_SURFACE = {
	borderRadius: 'var(--border-radius-200, 8px)',
	border: '1px solid rgba(207, 207, 207, 0.20)',
	background: 'rgba(250, 250, 250, 0.50)',
	backdropFilter: CARD_MENU_BLUR,
	WebkitBackdropFilter: CARD_MENU_BLUR,
	boxShadow: '1px 1px 6px 0 rgba(247, 95, 43, 0.08)',
	'.autonomos-scope &': {
		boxShadow: `1px 1px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/**
 * Cards OPACAS: mismo borde, radio y sombra que `CARD_SURFACE` pero con fondo
 * macizo. Para el paper de los modales centrados (los que llevan velo oscuro
 * detrás): ahi el cristal no se compone contra el degradado de la pagina sino
 * contra el backdrop, y el 50% de blanco sobre el negro del velo se ve gris.
 * Los modales centrados nuevos usan `MODAL_SURFACE` + `MODAL_OVERLAY_SURFACE`.
 */
export const CARD_SOLID = '#FAFAFA';

export const CARD_SURFACE_SOLID = {
	borderRadius: 'var(--border-radius-200, 8px)',
	border: '1px solid rgba(207, 207, 207, 0.20)',
	background: CARD_SOLID,
	boxShadow: `1px 1px 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		boxShadow: `1px 1px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/**
 * Superficie de inputs (Figma: Components/Inside Input).
 * Radio 4px, fondo 60% y sombra interior. Los consumidores hacen spread
 * sobre `.MuiInputBase-root`; el input interno se deja transparente.
 */
export const INPUT_SURFACE = {
	borderRadius: 'var(--border-radius-100, 4px) !important',
	background: 'var(--Background-bg-100, rgba(255, 255, 255, 0.60)) !important',
	backgroundColor: 'var(--Background-bg-100, rgba(255, 255, 255, 0.60)) !important',
	boxShadow: '2px 2px 4px 0 rgba(0, 0, 0, 0.04) inset !important',
};

export const BOX_SURFACE = {
	borderRadius: 'var(--border-radius-200, 8px)',
	padding: 8,
	background: 'var(--Background-bg-100, rgba(255, 255, 255, 0.60))',
	backdropFilter: BLUR,
	WebkitBackdropFilter: BLUR,
};

/**
 * Caja del slider (Figma: "Selector Onboarding", 312×80).
 * El padding de 10px es el hueco hasta la BOLA (y=10). El carril queda 11px
 * más abajo (y=21). Abajo, 10px tras el texto de 20px (y=50).
 */
export const SELECTOR_SURFACE = {
	...BOX_SURFACE,
	padding: 10,
};

/** Cabeceras: sombra hacia abajo (offset 0 2px), fondo 40%. */
export const HEADER_SURFACE = {
	background: 'rgba(255, 255, 255, 0.40)',
	backdropFilter: BLUR,
	WebkitBackdropFilter: BLUR,
	boxShadow: `${SHADOW_BORDER}, 0 2px 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		boxShadow: `${SHADOW_BORDER}, 0 2px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/**
 * Cabeceras OPACAS: mismo color aparente que `HEADER_SURFACE` pero sin dejar
 * pasar nada por detrás. Para cabeceras `position: fixed`, donde el contenido
 * scrollea por debajo y con el 40% translúcido se leería el texto a través.
 *
 * Los colores son el resultado de componer lo que hay debajo de una cabecera
 * normal: fondo #F5F5F5 + el degradado del scope (naranja / lila al 23%) + el
 * blanco al 40% de HEADER_SURFACE. Por eso se ve igual que el resto, pero macizo.
 */
export const HEADER_SOLID_PERSONAL = '#F9E4DD';
export const HEADER_SOLID_AUTONOMOS = '#EAE5F3';

export const HEADER_SURFACE_SOLID = {
	background: HEADER_SOLID_PERSONAL,
	boxShadow: `${SHADOW_BORDER}, 0 2px 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		background: HEADER_SOLID_AUTONOMOS,
		boxShadow: `${SHADOW_BORDER}, 0 2px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/**
 * Alto de la cabecera de mobile (`CustomHeader`: 16px arriba + 16px abajo + la
 * fila de 24px del título / icono de cierre). Es el hueco que tiene que dejar el
 * contenido cuando la cabecera va `position: fixed`, para que no scrollee por
 * debajo. Va aquí, junto a las superficies de cabecera, para no repetir el número
 * suelto (54, 56, 74, 100...) en cada pantalla.
 */
export const MOBILE_HEADER_HEIGHT = 56;

/**
 * Panel de firma OTP (`FirmOTP`). Márgenes del panel según diseño ("Panel OTP"):
 * 24px arriba y abajo, 16px a los lados. El hueco de 16px entre bloques lo ponen
 * los propios bloques del panel.
 *
 * Vive aquí porque la MISMA firma se pinta desde una decena de flujos (tarjetas,
 * bizum, fondos, dispositivos...) y cada pantalla venía sumando el padding de su
 * contenedor de paso (`15px 24px`, `pt24`, `mt54`...), así que la firma no tenía
 * los mismos márgenes en dos pantallas seguidas.
 *
 * Por eso el contenedor del paso NO debe añadir padding cuando el paso es la
 * firma: se suma a este y descoloca el panel. El hueco de una cabecera
 * `position: fixed` sí hace falta, pero se pone con `MOBILE_HEADER_HEIGHT`, no
 * con el padding de contenido.
 */
export const OTP_PANEL_SPACING = {
	paddingTop: 24,
	paddingRight: 16,
	paddingBottom: 24,
	paddingLeft: 16,
};

/**
 * Panel de resultado (ok / error) de una operación: el check, el título y los
 * textos del paso final. Márgenes de la pantalla de referencia ("Solicitar
 * tarjeta"): 24px arriba, 24px a los lados y 32px abajo.
 *
 * Igual que `OTP_PANEL_SPACING`, vive aquí porque cada flujo llevaba su propio
 * padding en el contenedor del paso (24, 18, 15, 13...) y el hueco entre la
 * cabecera y el check salía distinto en cada pantalla. Donde la cabecera va
 * `position: fixed` hay que sumarle `MOBILE_HEADER_HEIGHT` al `paddingTop`, que
 * es hueco de cabecera y no del panel.
 */
export const RESULT_PANEL_SPACING = {
	paddingTop: 24,
	paddingRight: 24,
	paddingBottom: 32,
	paddingLeft: 24,
};

/**
 * Cabeceras en kids/junior. El fondo NO es el degradado claro de personal /
 * autónomos, sino el degradado saturado naranja -> lila (`juniorKidsBackRoot`),
 * así que la separación va en BLANCO: con una sombra negra el borde inferior de
 * la cabecera se lee como una línea negra sobre el degradado.
 */
export const HEADER_SURFACE_KIDS = {
	background: 'var(--Background-bg-50, rgba(255, 255, 255, 0.10))',
	boxShadow:
		'0 0 0 1px rgba(255, 255, 255, 0.10), 0 2px 6px 0 rgba(255, 255, 255, 0.25)',
};

/**
 * Navbar inferior y botones fijos: sombra hacia arriba (offset 0 -2px), fondo 60%.
 * Lleva mas desenfoque que las cards (`NAVBAR_BLUR`, 8px): por debajo pasa todo el
 * contenido de la pantalla al scrollear, no un trozo de fondo.
 */
export const NAVBAR_SURFACE = {
	background: 'rgba(255, 255, 255, 0.60)',
	backdropFilter: NAVBAR_BLUR,
	WebkitBackdropFilter: NAVBAR_BLUR,
	boxShadow: `${SHADOW_BORDER}, 0 -2px 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		boxShadow: `${SHADOW_BORDER}, 0 -2px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/** Alto del menú inferior de mobile (`MobileMenu`). */
export const MOBILE_NAVBAR_HEIGHT = 60;

/** Cuánto sobresale el círculo central "Contratar" por encima del pill del menú. */
export const MOBILE_NAVBAR_CENTER_FAB_OVERFLOW = 28;

/**
 * Clase marcador de las barras de botones fijas que bajan hasta el fondo por detrás
 * del menú inferior. La lee `MobileMenu` con `body:has(...)` para NO pintar su propio
 * cristal cuando la pantalla ya pinta uno: dos capas al 60% componen ~84% de blanco y
 * se vería un escalón de tono (y una línea) justo en el borde superior del menú.
 */
export const BOTTOM_BAR_CLASS = 'bottom-glass-bar';

/**
 * Separador del menú inferior cuando comparte superficie con una barra de botones:
 * una línea de 1px (sombra `inset`, no borde, para no alterar el alto del navbar) que
 * marca dónde empieza el menú sin volver a pintar cristal encima.
 */
export const NAVBAR_SEPARATOR_LINE = 'inset 0 1px 0 0 rgba(207, 207, 207, 0.45)';

/**
 * Barra de botones fija de mobile (transferencias, bizum). Es UNA sola superficie de
 * cristal desde su borde superior hasta el fondo de la pantalla; el hueco del menú va
 * como `paddingBottom`, no como un borde donde acaba el fondo. Úsala junto con
 * `BOTTOM_BAR_CLASS` en el className.
 */
export const BOTTOM_BAR_SURFACE = {
	...NAVBAR_SURFACE,
	position: 'fixed' as const,
	bottom: 0,
	paddingBottom: MOBILE_NAVBAR_HEIGHT,
};

/**
 * Menú lateral (drawer): sombra hacia la izquierda (offset -1px 0), fondo 60%.
 * Lleva el mismo desenfoque que las cards (`CARD_MENU_BLUR`, 7px) para que el
 * contenido que queda por detrás no se lea nítido a través del cristal.
 */
export const MENU_SURFACE = {
	background: 'rgba(255, 255, 255, 0.60)',
	backdropFilter: CARD_MENU_BLUR,
	WebkitBackdropFilter: CARD_MENU_BLUR,
	boxShadow: `${SHADOW_BORDER}, -1px 0 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		boxShadow: `${SHADOW_BORDER}, -1px 0 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};

/**
 * Velo (backdrop) del menú lateral: desenfoque de la zona que queda fuera del
 * panel. El panel lleva el suyo en `MENU_SURFACE` (`CARD_MENU_BLUR`).
 */
export const MENU_OVERLAY_SURFACE = {
	backgroundColor: 'rgba(255, 255, 255, 0.05)',
	backdropFilter: MENU_OVERLAY_BLUR,
	WebkitBackdropFilter: MENU_OVERLAY_BLUR,
};

const MODAL_OVERLAY_BLUR = 'blur(25.5px)';

/**
 * Velo de las modales centradas (Figma: Modal background). No es el velo oscuro
 * de MUI: blanco al 5% + blur 25.5px, para que el paper de cristal se componga
 * contra la página y no contra un gris.
 */
export const MODAL_OVERLAY_SURFACE = {
	background: 'var(--Modal-background, rgba(255, 255, 255, 0.05))',
	backdropFilter: MODAL_OVERLAY_BLUR,
	WebkitBackdropFilter: MODAL_OVERLAY_BLUR,
};

/**
 * Paper de las modales centradas (Figma: Fondo modal). El `!important` gana al
 * `#fff` de `.MuiPaper-root`. La sombra es la de card (naranja / lila).
 */
export const MODAL_SURFACE = {
	borderRadius: 'var(--border-radius-200, 8px)',
	background: 'var(--Background-bg-100, rgba(255, 255, 255, 0.60)) !important',
	boxShadow: `1px 1px 6px 0 ${SHADOW_PERSONAL}`,
	'.autonomos-scope &': {
		boxShadow: `1px 1px 6px 0 ${SHADOW_AUTONOMOS}`,
	},
};
